import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "../context/ContentContext";
import Marquee from "./Marquee";
import VideoModal from "./VideoModal";
import ScrollCue from "./ScrollCue";
import LatestReleaseCarousel from "./LatestReleaseCarousel";
import SEO from "./SEO";

const ROTATE = ["creativity.", "craft.", "storytelling."];

const THREE_WAYS = [
  {
    number: "01",
    title: "Television Commercials",
    body:
      "From napkin-sketch brief to a finished, graded film — commercials built on a craft grammar that keeps an entire campaign looking like one world.",
    href: "/work#films",
  },
  {
    number: "02",
    title: "Print Media",
    body:
      "Key visuals, hero images and product art direction engineered to command a billboard and still land on a phone screen.",
    href: "/work#print",
  },
  {
    number: "03",
    title: "Content Engine",
    body:
      "Custom generative pipelines — reusable characters, locked styles and prompt engines wired into one system — so a single idea becomes films, frames and every format a campaign needs, on brand and in a fraction of the time.",
    href: "/capabilities/engines",
  },
];

const Home = () => {
  const { HERO_STILLS, HERO_VIDEO, LATEST_RELEASES, MARQUEE_LOGOS, BRAND } = useContent();
  const [stillIdx, setStillIdx] = useState(0);
  const [wordIdx, setWordIdx] = useState(0);
  const [videoId, setVideoId] = useState(null);
  const [videoMeta, setVideoMeta] = useState(null);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setStillIdx((i) => (i + 1) % HERO_STILLS.length);
      setWordIdx((i) => (i + 1) % ROTATE.length);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  return (
    <div className="bg-canvas text-ink pb-16 md:pb-20">
      <SEO
        title="SAMURAI · Cinematic AI Film Studio for Brands & Agencies"
        description="SAMURAI is a Mumbai and Goa based creative studio blending live-action craft, AI-led production and custom content engines for the world's best brands."
        path="/"
        image="https://samur-ai.in/samurai-logo.png"
      />
      {/* Hero */}
      <section className="dark-block relative min-h-[86vh] md:min-h-[92vh] flex items-end overflow-hidden pt-24 bg-canvas">
        <div className="absolute inset-0">
          {!videoFailed && (
            <video
              ref={videoRef}
              src={HERO_VIDEO}
              className="hero-video absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              onError={() => setVideoFailed(true)}
            />
          )}
          {videoFailed && (
            <div className="absolute inset-0">
              {HERO_STILLS.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-in-out ${
                    i === stillIdx ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
            </div>
          )}
          <div className="absolute inset-0 hero-scrim" />
          <div className="absolute inset-0 pointer-events-none noise-overlay" />
        </div>

        <div className="relative mx-auto px-6 md:px-10 w-full pb-10 md:pb-14">
          <h1
            className="font-heading font-black leading-[0.92] tracking-[-0.03em] fade-up"
            style={{ animationDelay: "120ms" }}
          >
            <span className="block text-[40px] md:text-[72px] uppercase">Building</span>
            <span className="block text-[40px] md:text-[72px] uppercase">more room</span>
            <span className="flex items-baseline gap-3 md:gap-5 flex-wrap">
              <span className="text-[40px] md:text-[72px] italic font-serif font-normal text-ink/80 lowercase">
                for
              </span>
              <span className="relative inline-block h-[46px] md:h-[80px] w-[280px] md:w-[560px] overflow-hidden align-baseline">
                {ROTATE.map((w, i) => (
                  <span
                    key={w}
                    className={`absolute left-0 top-0 text-[40px] md:text-[72px] leading-[0.92] italic font-serif font-normal text-ink lowercase transition-all duration-700 ease-out ${
                      i === wordIdx
                        ? "translate-y-0 opacity-100"
                        : "translate-y-full opacity-0"
                    }`}
                  >
                    {w}
                  </span>
                ))}
              </span>
            </span>
          </h1>
        </div>

        <ScrollCue right={BRAND.locations} />
      </section>

      {/* Three ways — TVC / Print / Content Engine */}
      <section
        className="pb-24 md:pb-32 pt-14 md:pt-20"
        data-testid="landing-three-ways"
      >
        <div className="mx-auto px-6 md:px-10 max-w-6xl">
          <h2
            className="mb-14 md:mb-20 mx-auto max-w-4xl text-center font-heading font-black uppercase leading-[1.05] tracking-[-0.02em] text-[22px] sm:text-[28px] md:text-[36px]"
            data-testid="three-ways-intro"
          >
            Three ways we put creativity and AI to work for the world&rsquo;s best brands
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {THREE_WAYS.map((w) => {
              const external = w.href.startsWith("http");
              const Wrapper = external ? "a" : Link;
              const linkProps = external
                ? { href: w.href }
                : { to: w.href };
              return (
                <Wrapper
                  key={w.number}
                  {...linkProps}
                  className="group relative flex flex-col justify-between h-full rounded-2xl border border-ink/12 p-7 md:p-8 hover:border-ink/40 hover:-translate-y-1 transition-all duration-500"
                  data-testid={`three-ways-card-${w.number}`}
                >
                  <div>
                    <h3 className="font-heading font-medium uppercase leading-[1.05] tracking-[-0.01em] text-[22px] md:text-[26px]">
                      {w.title}
                    </h3>
                    <p
                      className="mt-5 text-sm md:text-[15px] text-ink/70 leading-[1.6] font-light"
                      style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}
                    >
                      {w.body}
                    </p>
                  </div>
                  <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-ink/85 group-hover:text-ink transition-colors">
                    Explore More
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1" />
                  </span>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest release carousel */}
      <section className="py-20 md:py-28 border-t border-ink/10">
        <div className="mx-auto px-6 md:px-10">
          <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-8">
            Latest release
          </p>
          <LatestReleaseCarousel
            items={LATEST_RELEASES}
            onPlay={(id, meta) => {
              setVideoMeta(meta);
              setVideoId(id);
            }}
          />
        </div>
      </section>

      <VideoModal
        videoId={videoId}
        meta={videoMeta}
        open={!!videoId}
        onClose={() => {
          setVideoId(null);
          setVideoMeta(null);
        }}
      />

      {/* Fixed marquee — permanent bottom strip on the landing page. Always
          visible above every section including the footer. */}
      <div
        className="dark-block fixed bottom-0 left-0 right-0 z-40 py-3 md:py-4 border-t border-ink/10 bg-canvas pointer-events-none"
        data-testid="home-fixed-marquee"
      >
        <Marquee items={MARQUEE_LOGOS} speed={26} />
      </div>
    </div>
  );
};

export default Home;
